/**
 * WorkshopOpsChartCard
 * ────────────────────
 * Daily Entry view, top-of-page card with TWO charts side-by-side:
 *
 *  1. Turnover vs Parts ORP — daily bar chart (existing).
 *  2. Supplier Consumption — pie chart of per-supplier ORP $ across the
 *     same date range. Sourced from approved invoice line items only
 *     (quote-builder rows excluded). Helps the SA see who they owe.
 *
 * A single shared range toggle drives both charts so the SA can flip
 * Today / 7d / 30d / 90d / 365d / Custom and see both visualisations
 * update in sync.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  DollarSign,
  ReceiptText,
  Wallet,
  RefreshCw,
  PieChart as PieIcon,
  Truck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatAUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatTick = (d) => {
  if (!d) return '';
  const [, m, day] = d.split('-');
  return `${day}/${m}`;
};

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// Distinct colours for the supplier pie (10 hand-picked, then a deterministic
// fallback to keep large supplier lists readable).
const PIE_COLOURS = [
  '#10b981', '#f43f5e', '#3b82f6', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#a855f7', '#eab308', '#ef4444', '#0ea5e9',
];
const colourFor = (idx) => PIE_COLOURS[idx % PIE_COLOURS.length];

const RANGE_PRESETS = [
  { id: 'today', label: 'Today', days: 1 },
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
  { id: '365d', label: '365d', days: 365 },
  { id: 'custom', label: 'Custom', days: null },
];

export default function WorkshopOpsChartCard({ defaultRange = '30d' }) {
  const [activeRange, setActiveRange] = useState(defaultRange);
  // Custom range state (used only when activeRange === 'custom')
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6); return fmtDate(d);
  });
  const [customEnd, setCustomEnd] = useState(() => fmtDate(new Date()));

  const [series, setSeries] = useState([]);
  const [seriesTotals, setSeriesTotals] = useState({ turnover: 0, parts_orp_total: 0, invoice_count: 0, estimated_gross: 0 });
  const [suppliers, setSuppliers] = useState([]);
  const [supplierTotals, setSupplierTotals] = useState({ suppliers_count: 0, orp_total: 0, line_count: 0 });
  const [loading, setLoading] = useState(true);

  // Resolve actual start_date / end_date strings from the toggle state
  const { startDate, endDate, rangeLabel } = useMemo(() => {
    const end = new Date();
    if (activeRange === 'custom') {
      return {
        startDate: customStart,
        endDate: customEnd,
        rangeLabel: `${customStart} → ${customEnd}`,
      };
    }
    const preset = RANGE_PRESETS.find(p => p.id === activeRange) || RANGE_PRESETS[2];
    const start = new Date();
    start.setDate(end.getDate() - (preset.days - 1));
    return {
      startDate: fmtDate(start),
      endDate: fmtDate(end),
      rangeLabel: preset.id === 'today' ? 'Today' : `last ${preset.days} days`,
    };
  }, [activeRange, customStart, customEnd]);

  const load = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const [seriesRes, suppliersRes] = await Promise.all([
        fetch(
          `${BACKEND_URL}/api/super-admin/financials/workshop-ops-series?start_date=${startDate}&end_date=${endDate}`,
          { credentials: 'include' },
        ),
        fetch(
          `${BACKEND_URL}/api/super-admin/financials/supplier-consumption?start_date=${startDate}&end_date=${endDate}`,
          { credentials: 'include' },
        ),
      ]);
      if (seriesRes.ok) {
        const data = await seriesRes.json();
        setSeries(data.series || []);
        setSeriesTotals(data.totals || {});
      }
      if (suppliersRes.ok) {
        const data = await suppliersRes.json();
        setSuppliers(data.suppliers || []);
        setSupplierTotals(data.totals || {});
      }
    } catch (_e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, [startDate, endDate]);

  // Top-N suppliers for the pie (cap at 10 to keep readable); roll the rest
  // into "Others" so the slice count is sensible.
  const pieData = useMemo(() => {
    if (suppliers.length <= 10) return suppliers;
    const top = suppliers.slice(0, 9);
    const rest = suppliers.slice(9);
    const otherTotal = rest.reduce((s, x) => s + x.orp_total, 0);
    return [
      ...top,
      {
        supplier: `Others (${rest.length})`,
        orp_total: round2(otherTotal),
        line_count: rest.reduce((s, x) => s + x.line_count, 0),
        invoice_count: rest.reduce((s, x) => s + x.invoice_count, 0),
      },
    ];
  }, [suppliers]);

  return (
    <Card className="p-5 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/40 to-white" data-testid="workshop-ops-chart">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600" />
            Workshop Operations View — {rangeLabel}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Daily turnover (invoice totals) vs Parts ORP, and the per-supplier breakdown. Sourced from approved invoice line items — quote rows excluded.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-slate-200 rounded overflow-hidden" data-testid="workshop-ops-range-group">
            {RANGE_PRESETS.map(p => (
              <button
                key={p.id}
                onClick={() => setActiveRange(p.id)}
                className={`px-2.5 py-1 text-xs ${activeRange === p.id ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid={`workshop-ops-range-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="workshop-ops-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {activeRange === 'custom' && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="workshop-ops-custom-range">
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            From
            <Input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="h-8 text-xs w-36"
              data-testid="workshop-ops-custom-start"
            />
          </label>
          <label className="text-xs text-slate-600 flex items-center gap-1.5">
            To
            <Input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="h-8 text-xs w-36"
              data-testid="workshop-ops-custom-end"
            />
          </label>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-xs">
        <div className="border border-emerald-200 bg-emerald-50/40 rounded p-2">
          <p className="text-emerald-700 flex items-center gap-1"><DollarSign className="h-3 w-3" />Turnover</p>
          <p className="text-lg font-semibold text-emerald-700" data-testid="workshop-ops-total-turnover">
            {formatAUD(seriesTotals.turnover)}
          </p>
        </div>
        <div className="border border-rose-200 bg-rose-50/40 rounded p-2">
          <p className="text-rose-700 flex items-center gap-1"><Wallet className="h-3 w-3" />Parts ORP (to pay)</p>
          <p className="text-lg font-semibold text-rose-700" data-testid="workshop-ops-total-orp">
            {formatAUD(seriesTotals.parts_orp_total)}
          </p>
        </div>
        <div className={`border rounded p-2 ${
          (seriesTotals.estimated_gross || 0) >= 0
            ? 'border-blue-200 bg-blue-50/40'
            : 'border-amber-200 bg-amber-50/40'
        }`}>
          <p className={`flex items-center gap-1 ${(seriesTotals.estimated_gross || 0) >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
            <Activity className="h-3 w-3" />Est. gross (turnover − ORP)
          </p>
          <p className={`text-lg font-semibold ${(seriesTotals.estimated_gross || 0) >= 0 ? 'text-blue-700' : 'text-amber-700'}`} data-testid="workshop-ops-est-gross">
            {formatAUD(seriesTotals.estimated_gross)}
          </p>
        </div>
        <div className="border border-slate-200 rounded p-2">
          <p className="text-slate-500 flex items-center gap-1"><ReceiptText className="h-3 w-3" />Invoices</p>
          <p className="text-lg font-semibold text-slate-700" data-testid="workshop-ops-invoice-count">
            {seriesTotals.invoice_count || 0}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT — Turnover vs ORP daily bar chart */}
        <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="workshop-ops-bar-card">
          <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5 text-emerald-600" />
            Daily Turnover vs Parts ORP
          </p>
          <div className="h-64">
            {series.length === 0 && !loading ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">
                No invoices in this range yet
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 5, right: 15, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatTick} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v, key) => [
                      formatAUD(v),
                      key === 'turnover' ? 'Turnover' : 'Parts ORP',
                    ]}
                    labelFormatter={(d) => `Day: ${d}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="turnover" name="Turnover" fill="#10b981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="parts_orp_total" name="Parts ORP" fill="#f43f5e" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* RIGHT — Supplier consumption pie chart */}
        <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="workshop-ops-pie-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <PieIcon className="h-3.5 w-3.5 text-rose-600" />
              Supplier Consumption (ORP $)
            </p>
            <p className="text-[10px] text-slate-500" data-testid="workshop-ops-supplier-count">
              {supplierTotals.suppliers_count} supplier{supplierTotals.suppliers_count === 1 ? '' : 's'} · {formatAUD(supplierTotals.orp_total)} total
            </p>
          </div>
          <div className="h-64 flex">
            {pieData.length === 0 && !loading ? (
              <div className="w-full flex items-center justify-center text-sm text-slate-400">
                No supplier ORP recorded in this range
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="orp_total"
                        nameKey="supplier"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={92}
                        paddingAngle={2}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={colourFor(i)} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, _name, item) => [
                          formatAUD(v),
                          item.payload.supplier,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="w-44 overflow-y-auto pl-2 text-[11px] space-y-1" data-testid="workshop-ops-supplier-legend">
                  {pieData.map((s, i) => (
                    <li
                      key={s.supplier}
                      className="flex items-center justify-between gap-2"
                      data-testid={`workshop-ops-supplier-${i}`}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ background: colourFor(i) }}
                        />
                        <span className="truncate text-slate-700" title={s.supplier}>
                          {s.supplier}
                        </span>
                      </span>
                      <span className="font-semibold text-slate-700 flex-shrink-0">
                        {formatAUD(s.orp_total)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
            <Truck className="h-2.5 w-2.5" />
            Per-supplier ORP cost across approved invoice line items.
          </p>
        </div>
      </div>

      <p className="text-[10px] text-slate-400 mt-3">
        ORP is calculated only from invoice line items with a supplier cost recorded. Quote-builder rows are excluded until they're added to invoice lines.
      </p>
    </Card>
  );
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
